import React, { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Search, MapPin, Star, Loader2, X } from 'lucide-react';
import { SEARCH_PLACES } from '../../graphql/queries/places';

export interface PlaceResult {
  id: string;
  source: string;
  name: string;
  address: string | null;
  city: string | null;
  country: string | null;
  stars: string | null;
  image: string | null;
  website: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface Props {
  /** Appelé quand l'utilisateur sélectionne un résultat. */
  onSelect: (place: PlaceResult) => void;
  placeholder?: string;
}

/**
 * Recherche d'hôtel par nom. Le backend interroge Google Places (si configuré)
 * ou OpenStreetMap Nominatim. À la sélection d'un résultat, le parent reçoit
 * un objet normalisé pour remplir les champs du formulaire.
 */
export default function HotelSearchInput({ onSelect, placeholder }: Props) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [runSearch, { data, loading }] = useLazyQuery<{ searchPlaces: PlaceResult[] }>(
    SEARCH_PLACES,
    { fetchPolicy: 'no-cache' }
  );

  // Debounce de 350ms
  useEffect(() => {
    if (term.trim().length < 2) return;
    const id = setTimeout(() => {
      runSearch({ variables: { query: term.trim() } });
      setOpen(true);
    }, 350);
    return () => clearTimeout(id);
  }, [term, runSearch]);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const results = data?.searchPlaces || [];

  const handlePick = (p: PlaceResult) => {
    onSelect(p);
    setTerm('');
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => term.trim().length >= 2 && setOpen(true)}
          placeholder={placeholder || 'Rechercher un hôtel par nom (ex: Sheraton Alger)…'}
          className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
        {!loading && term && (
          <button
            type="button"
            onClick={() => {
              setTerm('');
              setOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && term.trim().length >= 2 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
          {loading && results.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">Recherche…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              Aucun résultat. Vous pouvez saisir les informations manuellement.
            </div>
          ) : (
            <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100">
              {results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(r)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                          <span className="truncate">{r.name}</span>
                          {r.stars && (
                            <span className="inline-flex items-center gap-0.5 text-xs text-yellow-600 shrink-0">
                              <Star className="w-3 h-3 fill-yellow-400" />
                              {r.stars}
                            </span>
                          )}
                        </div>
                        {r.address && (
                          <div className="text-xs text-gray-500 truncate">{r.address}</div>
                        )}
                      </div>
                      <span className="text-[10px] uppercase text-gray-400 font-semibold tracking-wider shrink-0">
                        {r.source === 'google' ? 'Google' : r.source === 'geoapify' ? 'Geoapify' : 'OSM'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
